import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTransactionDetailsData } from '../../hooks/useReviewDetailsData';
import Loader from '../../components/FormsUI/Loader';
import useAuth from '../../hooks/useAuth';
import { useLogout } from '../../hooks/useLogout';

const ALL_STEP_CONFIG = [
  {
    key: 'IPV',
    label: 'Live Picture',
    route: '/signatory-ipv',
    icon: 'camera_alt',
    flag: 'ipvRequired',
  },
  {
    key: 'Document',
    label: 'Document Details',
    route: '/signatory-documents',
    icon: 'camera_alt',
    flag: 'isDocumentRequired',
  },
  {
    key: 'Consent',
    label: 'Consent',
    route: '/consent-details',
    icon: 'verified_user',
    flag: 'isConsentRequired',
  },
  {
    key: 'Review',
    label: 'Review',
    route: '/signatory-review',
    icon: 'description',
    flag: null,
  },
  { key: 'ESign', label: 'E-Sign', route: '/esign', icon: 'draw', flag: null },
];

const SignatoryLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [transData, setTransData] = useState({});
  const { auth, setAuth } = useAuth();
  const { mutate: logout } = useLogout();
  const state = location.state || {};
  const { ipvRequired, isConsentRequired } = state;
  const isAssistedMode = sessionStorage.getItem('isAssistedMode') === 'true';
  // Computed up front (no early return before this point) so the hooks below
  // that depend on activeStep are always called in the same order every render.
  const dynamicSteps = ALL_STEP_CONFIG.filter((step) => {
    if (step.flag === 'ipvRequired') return !!ipvRequired;
    if (step.flag === 'isConsentRequired') return !!isConsentRequired;
    return true;
  });

  const currentRoute = location.pathname;
  const foundIndex = dynamicSteps.findIndex((s) => s.route === currentRoute);
  const activeStep = foundIndex === -1 ? dynamicSteps.length - 1 : foundIndex;

  const [completedStep, setCompletedStep] = useState(activeStep);

  React.useEffect(() => {
    if (activeStep > completedStep) {
      setCompletedStep(activeStep);
    }
  }, [activeStep, completedStep]);

  // ReviewDetails toggles this via setIsCompleted(true/false) — controls whether
  // its "Edit" buttons are shown (hidden once submitted, shown again if editing reopens)
  const [isCompleted, setIsCompleted] = useState(false);

  // Fetch transaction details from API (same as StepperSection)
  const { status, data, isLoading: isLoadingTransaction } = useTransactionDetailsData();

  // Handle API response - same pattern as StepperSection
  useEffect(() => {
    if (status === 'success' && data) {
      const decryptedData = data?.data?.body;
      setTransData(decryptedData);
    }
  }, [status, data]);

  // Prevent back navigation for signatory flow

  useEffect(() => {
    if (!state.isSignatory) return;
  }, [state.isSignatory]); // Show loader while fetching transaction data

  useEffect(() => {
    const storedQuery = sessionStorage.getItem('query') || '';
    const navEntry = performance.getEntriesByType('navigation')[0];
    const wasReloaded = navEntry?.type === 'reload';

    const alreadyHandledThisReload = sessionStorage.getItem('reloadLogoutHandled') === 'true';

    window.history.pushState(null, '', window.location.href);

    const handleLogout = () => {
      const token = auth?.accessToken;
      setAuth({});
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('KycMode');
      if (token) logout(token);

      const assistedModeValue = sessionStorage.getItem('isAssistedMode');
      const signatoryMode = sessionStorage.getItem('isSignatory');

      if (assistedModeValue === 'true') {
        sessionStorage.setItem('isAssistedMode', 'false');
        sessionStorage.setItem('isAMLogout', 'true');
        setRedirect({
          to: '/rmlogin',
          replace: true,
          state: { data: 'expired' },
        });
      } else if (signatoryMode === 'true' && storedQuery) {
        setRedirect({
          to: `/signatory-login${storedQuery}`,
          replace: true,
          state: { data: 'expired' },
        });
      } else {
        setRedirect({
          to: `/login${storedQuery}`,
          replace: true,
          state: { data: 'expired' },
        });
      }
    };

    if (wasReloaded && !alreadyHandledThisReload) {
      sessionStorage.setItem('reloadLogoutHandled', 'true');
      return handleLogout();
    }

    const onBack = () => {
      handleLogout();
    };

    window.addEventListener('popstate', onBack);
    return () => window.removeEventListener('popstate', onBack);
  }, [auth?.accessToken, logout, setAuth]);
  const clickAbleMenuBtn = (index, isCompletedVal) => {
    // allow current + completed steps
    if (!(isCompletedVal || index === activeStep)) return;

    navigate(dynamicSteps[index].route, {
      state: location.state,
    });

    setIsSidebarOpen(false);
  };

  // Moves exactly one step back in the route sequence. No-op on the first step.
  const handleBack = () => {
    if (activeStep <= 0) return;
    const prevStep = dynamicSteps[activeStep - 1];
    navigate(prevStep.route, { state }); // keep isSignatory/etc. alive on the previous page
  };

  const SIGNATORY_ALLSTEPS = dynamicSteps.reduce((acc, step) => {
    acc[step.key] = step.key;
    return acc;
  }, {});
  const signatoryDynamicSteps = dynamicSteps.map((step) => step.key);

  // "setActiveStep" here means "go to this step's route" rather than a local index update
  const handleEditFromReview = (stepIndex) => {
    const targetStep = dynamicSteps[stepIndex];
    if (targetStep) {
      navigate(targetStep.route, { state });
    }
  };

  const currentStepKey = dynamicSteps[activeStep]?.key;
  const reviewExtraProps =
    currentStepKey === 'Review'
      ? {
          ALLSTEPS: SIGNATORY_ALLSTEPS,
          dynamicSteps: signatoryDynamicSteps,
          setActiveStep: handleEditFromReview,
          isCompleted,
          setIsCompleted,
        }
      : {};

  const totalSteps = dynamicSteps.length || 1;
  const progressPercent = ((activeStep + 1) / totalSteps) * 100;
  const mobileProgressPercent = Math.round((activeStep / totalSteps) * 100);

  // Pass handleBack (always) plus the Review-only props down to the active step component
  const childWithProps = React.isValidElement(children)
    ? React.cloneElement(children, { handleBack, ...reviewExtraProps })
    : children;
  if (redirect) {
    return <Navigate to={redirect.to} replace={redirect.replace} state={redirect.state} />;
  }
  if (isLoadingTransaction) {
    return <Loader />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        // overflow: 'hidden',
      }}
    >
      <main
        id="main-content"
        className="ob2-layout"
        style={{ flex: 1, width: '100%', paddingTop: '72px' }}
        tabIndex="-1"
      >
        {/* Left side: Form content */}
        <div className={`ob2-left${isAssistedMode ? ' full-width' : ''}`}>
          {!isAssistedMode && (
            <button
              className="mobile-progress-toggle"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="View Progress"
            >
              <span className="material-symbols-outlined">analytics</span>
              <span>{mobileProgressPercent}% Complete</span>
              <span className="material-symbols-outlined text-xl-icon">list</span>
            </button>
          )}
          <div className="ob2-left-content">{childWithProps}</div>
        </div>

        {/* Mobile Overlay */}
        {!isAssistedMode && isSidebarOpen && (
          <div className="sidebar-overlay active" onClick={() => setIsSidebarOpen(false)} />
        )}

        {/* Right side: Sidebar */}
        {!isAssistedMode && (
          <div className={`ob2-right ${isSidebarOpen ? 'open' : ''}`}>
            <div className="mobile-sidebar-header">
              <h3>Application Progress</h3>
              <button className="close-sidebar" onClick={() => setIsSidebarOpen(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="session-middle">
              <div className="session-status">
                <h4>{transData?.serviceName}</h4>
              </div>
            </div>

            <div className="ob2-session-card">
              <div className="session-top">
                <div className="session-status">
                  <span className="status-dot" />
                  <span className="status-text">ACTIVE SESSION</span>
                </div>
                <span className="material-symbols-outlined session-icon">verified_user</span>
              </div>
              <div className="session-middle">
                <span className="session-label">SIGNATORY NAME</span>
                <h3 className="session-name">{transData?.signatoryName || 'N/A'}</h3>
              </div>
              <div className="session-bottom">
                <div className="pan-block">
                  <span className="session-label">PAN IDENTIFIER</span>
                  <div className="pan-value">{transData?.signatoryPan || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="ob2-vertical-stepper">
              {dynamicSteps.map((step, index) => {
                const isCompletedIndex = index < completedStep;
                const isActive = index === activeStep;
                const isPending = index > completedStep;
                return (
                  <div
                    key={step.key}
                    className={`ob2-v-step${isActive ? ' active' : ''}${isCompletedIndex ? ' completed' : ''}${isPending ? ' pending' : ''}`}
                    onClick={() => clickAbleMenuBtn(index, isCompletedIndex)}
                    style={{ cursor: isCompletedIndex ? 'pointer' : 'default' }}
                  >
                    <div className="ob2-v-icon">
                      {isCompletedIndex ? (
                        <span className="material-symbols-outlined check-icon">check_circle</span>
                      ) : isActive ? (
                        <div className="active-icon-wrapper">
                          <span className="material-symbols-outlined">{step.icon}</span>
                        </div>
                      ) : (
                        <span className="material-symbols-outlined pending-icon">{step.icon}</span>
                      )}
                    </div>
                    <div className="ob2-v-label">{step.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="ob2-right-footer">
              <div className="ob2-progress-box">
                <div className="ob2-progress-text">
                  <span>PROGRESS</span>
                  <span className="ob2-progress-count">
                    {activeStep + 1}/{totalSteps}
                  </span>
                </div>
                <div className="ob2-progress-bar">
                  <div className="ob2-progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SignatoryLayout;
